import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wordmark } from './Logo';
import { WhatsAppIcon, WHATSAPP_URL } from './WhatsAppButton';
import { useLang, LANGS } from '../i18n';

const LINKS = [
    { id: '#historia', key: 'nav_historia' },
    { id: '#mision', key: 'nav_mision' },
    { id: '#vision', key: 'nav_vision' },
    { route: '/servicios', key: 'nav_servicios', sub: 'servicios' },
    { id: '#casos', key: 'nav_casos', sub: 'casos' },
];

const SERVICE_SUBS = [
    { route: '/servicios/branding', key: 'sp_name_branding', testid: 'branding' },
    { route: '/servicios/digitalizacion', key: 'sp_name_digitalizacion', testid: 'digitalizacion' },
    { route: '/servicios/automatizacion', key: 'sp_name_automatizacion', testid: 'automatizacion' },
    { route: '/servicios/seo-sem', key: 'sp_name_seo-sem', testid: 'seo-sem' },
    { route: '/servicios/growth-partner', key: 'sp_name_growth-partner', testid: 'growth-partner' },
];

const CASE_SUBS = [
    { route: '/casos/el-gato-con-copas', label: 'El Gato con Copas', testid: 'caso-1' },
    { route: '/casos/la-patrona', label: 'La Patrona', testid: 'caso-2' },
    { route: '/casos/burek', label: 'Burek', testid: 'caso-3' },
];

const Chevron = ({ open }) => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        aria-hidden="true"
        style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }}
    >
        <path d="M6 9l6 6 6-6" />
    </svg>
);

function LanguageDropdown() {
    const { lang, setLang, t } = useLang();
    const [openLang, setOpenLang] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const fn = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpenLang(false);
        };
        document.addEventListener('mousedown', fn);
        return () => document.removeEventListener('mousedown', fn);
    }, []);

    const current = LANGS.find((l) => l.code === lang) || LANGS[0];

    return (
        <div className="relative" ref={ref} data-testid="header-lang">
            <button
                onClick={() => setOpenLang(!openLang)}
                className="inline-flex items-center justify-center gap-1.5 h-10 min-w-[3.25rem] px-2.5 rounded-full border border-ink/15 text-[12px] font-bold tracking-[0.08em] text-ink hover:border-crimson hover:text-crimson transition-colors"
                aria-label={t('footer_lang')}
                aria-expanded={openLang}
                data-testid="header-lang-button"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3z" />
                </svg>
                {current.label}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true" style={{ transform: openLang ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>

            <div
                className={`absolute right-0 top-12 w-36 rounded-2xl bg-white border border-ink/10 shadow-[0_24px_50px_-20px_rgba(27,42,74,0.4)] overflow-hidden transition-all duration-300 origin-top ${openLang ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}
                data-testid="header-lang-menu"
            >
                {LANGS.map((l) => (
                    <button
                        key={l.code}
                        onClick={() => { setLang(l.code); setOpenLang(false); }}
                        className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] font-bold transition-colors ${lang === l.code ? 'text-crimson bg-pastelpink/40' : 'text-ink/70 hover:bg-canvas'}`}
                        data-testid={`header-lang-option-${l.code}`}
                    >
                        {l.label}
                        {lang === l.code && <span className="w-1.5 h-1.5 rounded-full bg-crimson" />}
                    </button>
                ))}
            </div>
        </div>
    );
}

function NavDropdown({ item, t, go }) {
    const [open, setOpen] = useState(false);
    const subs = item.sub === 'servicios' ? SERVICE_SUBS : CASE_SUBS;
    return (
        <div
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            data-testid={`nav-dropdown-${item.sub}`}
        >
            <button
                onClick={() => go(item)}
                className="ink-link inline-flex items-center gap-1 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink/70 hover:text-ink transition-colors"
                aria-haspopup="true"
                aria-expanded={open}
                data-testid={`nav-link-${item.key.replace('nav_', '')}`}
            >
                {t(item.key)}
                <Chevron open={open} />
            </button>
            <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 ${open ? '' : 'pointer-events-none'}`}>
                <div
                    className={`w-56 rounded-2xl bg-white border border-ink/10 shadow-[0_24px_50px_-20px_rgba(27,42,74,0.4)] overflow-hidden transition-all duration-300 origin-top ${open ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                    data-testid={`nav-menu-${item.sub}`}
                >
                    {subs.map((s) => (
                        <button
                            key={s.route || s.id}
                            onClick={() => { setOpen(false); go(s); }}
                            className="w-full text-left px-5 py-3 text-[13px] font-semibold text-ink/75 hover:text-crimson hover:bg-canvas transition-colors"
                            data-testid={`nav-sub-${s.testid}`}
                        >
                            {s.label || t(s.key)}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [openSub, setOpenSub] = useState(null);
    const navigate = useNavigate();
    const { t } = useLang();

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 24);
        fn();
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);

    const go = (item) => {
        setOpen(false);
        setOpenSub(null);
        if (item.route) {
            navigate(item.route);
            return;
        }
        const el = document.querySelector(item.id);
        if (el) {
            if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
            else el.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate('/', { state: { scrollTo: item.id } });
        }
    };

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled || open ? 'bg-canvas/85 backdrop-blur-xl border-b border-ink/10' : 'bg-transparent border-b border-transparent'}`}
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-[76px]">
                    <button
                        onClick={() => navigate('/')}
                        className="shrink-0"
                        aria-label="Ir al inicio"
                        data-testid="header-logo-button"
                    >
                        <Wordmark />
                    </button>

                    <nav className="hidden lg:flex items-center gap-7">
                        {LINKS.map((l) => l.sub ? (
                            <NavDropdown key={l.route || l.id} item={l} t={t} go={go} />
                        ) : (
                            <button
                                key={l.route || l.id}
                                onClick={() => go(l)}
                                className="ink-link text-[13px] font-semibold uppercase tracking-[0.14em] text-ink/70 hover:text-ink transition-colors"
                                data-testid={`nav-link-${l.key.replace('nav_', '')}`}
                            >
                                {t(l.key)}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <LanguageDropdown />
                        <a
                            href={WHATSAPP_URL(t('wa_general'))}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contactar por WhatsApp"
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366] text-white shadow-sm transition-all hover:scale-105 active:scale-95"
                            data-testid="header-whatsapp"
                        >
                            <WhatsAppIcon size={18} />
                        </a>
                        <button
                            onClick={() => go({ id: '#agenda' })}
                            className="hidden sm:inline-flex btn-shine items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-[13px] font-bold uppercase tracking-[0.12em] text-pastelcream transition-all hover:bg-crimsondark hover:scale-[1.03] active:scale-95"
                            data-testid="header-agenda-cta"
                        >
                            {t('cta_agendar')}
                        </button>
                        <button
                            className="lg:hidden inline-flex flex-col justify-center items-center w-10 h-10 rounded-full border border-ink/15"
                            onClick={() => setOpen(!open)}
                            aria-label="Abrir menú"
                            data-testid="header-menu-toggle"
                        >
                            <span className={`block w-4 h-[1.5px] bg-ink transition-transform ${open ? 'rotate-45 translate-y-[3px]' : ''}`} />
                            <span className={`block w-4 h-[1.5px] bg-ink mt-1 transition-transform ${open ? '-rotate-45 -translate-y-[3px]' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>

            <div
                className={`lg:hidden overflow-y-auto overflow-x-hidden transition-[max-height] duration-500 bg-canvas/95 backdrop-blur-xl ${open ? 'max-h-[calc(100vh-76px)] border-b border-ink/10' : 'max-h-0 overflow-hidden'}`}
                data-testid="mobile-menu"
            >
                <nav className="px-6 py-4 flex flex-col gap-1">
                    {LINKS.map((l) => l.sub ? (
                        <div key={l.route || l.id} className="border-b border-ink/5 last:border-0" data-testid={`mobile-nav-group-${l.sub}`}>
                            <button
                                onClick={() => setOpenSub(openSub === l.sub ? null : l.sub)}
                                className="w-full flex items-center justify-between py-3 font-display text-lg text-ink"
                                aria-expanded={openSub === l.sub}
                                data-testid={`mobile-nav-link-${l.key.replace('nav_', '')}`}
                            >
                                {t(l.key)}
                                <Chevron open={openSub === l.sub} />
                            </button>
                            <div className={`overflow-hidden transition-[max-height] duration-300 ${openSub === l.sub ? 'max-h-96' : 'max-h-0'}`} data-testid={`mobile-nav-sub-${l.sub}`}>
                                {l.sub === 'servicios' && (
                                    <button
                                        onClick={() => go({ route: '/servicios' })}
                                        className="w-full text-left py-2.5 pl-4 text-[14px] font-bold text-crimson"
                                        data-testid="mobile-nav-sub-ver-todos"
                                    >
                                        {t('sv_ver_todos')}
                                    </button>
                                )}
                                {(l.sub === 'servicios' ? SERVICE_SUBS : CASE_SUBS).map((s) => (
                                    <button
                                        key={s.route || s.id}
                                        onClick={() => go(s)}
                                        className="w-full text-left py-2.5 pl-4 text-[14px] font-semibold text-ink/70"
                                        data-testid={`mobile-nav-sub-${s.testid}`}
                                    >
                                        {s.label || t(s.key)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <button
                            key={l.route || l.id}
                            onClick={() => go(l)}
                            className="text-left py-3 font-display text-lg text-ink border-b border-ink/5 last:border-0"
                            data-testid={`mobile-nav-link-${l.key.replace('nav_', '')}`}
                        >
                            {t(l.key)}
                        </button>
                    ))}
                    <button
                        onClick={() => go({ id: '#agenda' })}
                        className="mt-2 mb-3 btn-shine rounded-full bg-crimson px-5 py-3 text-[13px] font-bold uppercase tracking-[0.12em] text-pastelcream"
                        data-testid="mobile-agenda-cta"
                    >
                        {t('cta_agendar')}
                    </button>
                </nav>
            </div>
        </header>
    );
}
